/**
 * AttendanceReport.js — dark-glass futuristic redesign
 * Summary table with inline drill-down · BUG FIX: from_date / to_date params
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  FaCalendarAlt, FaCheckCircle, FaTimesCircle, FaClock,
  FaUsers, FaLayerGroup, FaFilter, FaChartBar,
  FaPrint, FaExclamationCircle, FaTimes, FaChevronDown, FaChevronUp,
  FaUserCheck, FaSortAmountDown,
} from "react-icons/fa";
import { Colors } from "./branding";
import { useAppConfig } from "./AppContext";
import API_BASE from "./apiConfig";
import { useTheme, getT, getBrandColors} from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import SharedStatusBadge from "./components/StatusBadge";
import { useSort } from "./hooks/useSort";

const BASE = API_BASE;

// ─── Design tokens (non-theme, static) ─────────────────────────────────────────
const BRAND = {
  get blue() { return getBrandColors().blue; },
  get yellow() { return getBrandColors().yellow; },
  blur:     "blur(20px)",
  font:     "'Segoe UI', system-ui, sans-serif",
};

const glassCard = (T) => ({
  background: T.glass,
  backdropFilter: BRAND.blur, WebkitBackdropFilter: BRAND.blur,
  border: T.glassBdr,
  borderRadius: 16,
  boxShadow: T.glassSh,
});

// Present/Absent/Late resolve through T.statusPill(tone) (see StatusBadge below)
// so tenant-customized success/danger/warning colors + style preset apply.
// Excused has no home in the 5-tone system, so it stays a literal purple —
// matching AttendanceKiosk.js.
const STATUS_TONE = { Present: "success", Absent: "danger", Late: "warning" };
const EXCUSED_META = { color:"#a78bfa", bg:"rgba(167,139,250,0.12)", border:"rgba(167,139,250,0.30)" };

// Resolves a status label to {color, bg, border} — border as a plain color
// (matching this file's existing usage, e.g. borderColor:m.border) even though
// T.statusPill() returns a full "1px solid <color>" border shorthand.
function statusMeta(status, T) {
  const tone = STATUS_TONE[status];
  if (tone) {
    const p = T.statusPill(tone);
    return { color: p.color, bg: p.background, border: p.border.replace(/^[\d.]+px\s+solid\s+/, "") };
  }
  if (status === "Excused") return EXCUSED_META;
  return null;
}

const today   = () => new Date().toISOString().slice(0,10);
const daysAgo = (n) => { const d=new Date(); d.setDate(d.getDate()-n); return d.toISOString().slice(0,10); };
const fmtDate = (ds) => {
  if (!ds) return "—";
  const d = new Date(ds+"T00:00:00");
  return `${["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d.getDay()]}, ${
    ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][d.getMonth()]
  } ${d.getDate()}`;
};

// ─── Sub-components ────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const Icon = status==="Present"?FaCheckCircle:status==="Absent"?FaTimesCircle:status==="Late"?FaClock:FaUsers;
  const tone = STATUS_TONE[status];
  // Present/Absent/Late map onto the shared 5-tone system, so they route
  // through the real StatusBadge component (theme-driven). Excused (and any
  // unknown status) has no home in that tone system, so it keeps its literal
  // purple/neutral pill exactly as before — see statusMeta()'s comment.
  if (tone) return <SharedStatusBadge label={status} tone={tone} icon={<Icon size={9}/>} />;
  const s = status === "Excused" ? EXCUSED_META : { color: T.textMuted, bg: T.glass, border: T.inputBdr };
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 11px",
      borderRadius:20, fontSize:11, fontWeight:700,
      background:s.bg, color:s.color, border:`1px solid ${s.border}`,
      letterSpacing:"0.04em", whiteSpace:"nowrap" }}>
      <Icon size={9}/> {status}
    </span>
  );
}

function StatCard({ icon:Icon, label, value, color, glowColor }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const gc = glassCard(T);
  return (
    <div style={{ ...gc, padding:"14px 20px", display:"flex", alignItems:"center", gap:14, flex:1, minWidth:110 }}>
      <div style={{ width:40, height:40, borderRadius:11, background:`${glowColor||color}18`,
        border:`1px solid ${glowColor||color}35`,
        display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
        boxShadow:`0 0 14px ${glowColor||color}25` }}>
        <Icon size={17} style={{ color }}/>
      </div>
      <div>
        <div style={{ fontSize:26, fontWeight:900, color, lineHeight:1,
          textShadow:`0 0 16px ${glowColor||color}60` }}>{value}</div>
        <div style={{ fontSize:10, fontWeight:700, color:T.textMuted,
          textTransform:"uppercase", letterSpacing:"0.08em", marginTop:2 }}>{label}</div>
      </div>
    </div>
  );
}

function PctBar({ pct }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const c = pct>=80?getBrandColors().green:pct>=60?getBrandColors().orange:getBrandColors().red;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
      <div style={{ flex:1, height:5, borderRadius:4, background:T.divider, overflow:"hidden" }}>
        <div style={{ width:`${pct}%`, height:"100%",
          background:`linear-gradient(90deg,${c},${c}cc)`,
          borderRadius:4, boxShadow:`0 0 8px ${c}80` }}/>
      </div>
      <span style={{ fontSize:12, fontWeight:800, color:c, minWidth:34, textAlign:"right" }}>{pct}%</span>
    </div>
  );
}

// ─── Styled input wrapper ────────────────────────────────────────────────────
const inputStyle = (T) => ({
  padding:"8px 12px", borderRadius:9,
  border:`1.5px solid ${T.inputBdr}`,
  background:T.inputBg,
  backdropFilter:"blur(10px)", WebkitBackdropFilter:"blur(10px)",
  fontSize:13, color:T.inputText,
  fontFamily:BRAND.font, outline:"none",
  colorScheme: T.colorScheme,
});

// ─── Main component ────────────────────────────────────────────────────────────
export default function AttendanceReport() {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const gc = glassCard(T);
  const { config }  = useAppConfig();
  const primary     = config.primary_color   || BRAND.blue;
  const secondary   = config.secondary_color || BRAND.yellow;
  const fontFam     = `'${config.font_family||"Segoe UI"}', system-ui, sans-serif`;

  const [fromDate,     setFromDate]     = useState(daysAgo(30));
  const [toDate,       setToDate]       = useState(today());
  const [batchId,      setBatchId]      = useState("");
  const [batches,      setBatches]      = useState([]);
  const [summary,      setSummary]      = useState([]);
  const [drillDate,    setDrillDate]    = useState(null);
  const [drillRows,    setDrillRows]    = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [drillLoad,    setDrillLoad]    = useState(false);
  const [error,        setError]        = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [sortField,    setSortField]    = useState("student_name");
  const [sortAsc,      setSortAsc]      = useState(true);

  useEffect(() => {
    fetch(`${BASE}/batches/list`).then(r=>r.ok?r.json():[]).then(d=>setBatches(Array.isArray(d)?d:(d.batches||[]))).catch(()=>{});
  }, []);

  // ── FIX: use from_date / to_date to match backend params ──────────────────
  const fetchSummary = useCallback(async () => {
    if (!fromDate || !toDate) return;
    setLoading(true); setError(null); setDrillDate(null); setDrillRows([]);
    try {
      const params = new URLSearchParams({ from_date:fromDate, to_date:toDate });
      if (batchId) params.append("batch_id", batchId);
      const res = await fetch(`${BASE}/attendance/summary?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setSummary(d.summary || []);
    } catch (e) {
      setError("Could not load attendance summary. " + e.message);
    } finally { setLoading(false); }
  }, [fromDate, toDate, batchId]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const fetchDrill = async (date) => {
    if (drillDate === date) { setDrillDate(null); setDrillRows([]); return; }
    setDrillDate(date); setDrillLoad(true); setDrillRows([]); setStatusFilter("");
    try {
      const params = new URLSearchParams({ date });
      if (batchId) params.append("batch_id", batchId);
      const res = await fetch(`${BASE}/attendance/daily?${params}`);
      const d   = await res.json();
      setDrillRows(d.records || []);
    } catch {}
    finally { setDrillLoad(false); }
  };

  const totals = summary.reduce((acc,r) => ({
    present: acc.present+(r.present||0),
    absent:  acc.absent +(r.absent ||0),
    late:    acc.late   +(r.late   ||0),
    total:   acc.total  +(r.total  ||0),
  }), { present:0, absent:0, late:0, total:0 });

  const overallPct = totals.total>0 ? Math.round((totals.present/totals.total)*100) : 0;

  const summaryWithPct = summary.map(r => ({ ...r, pct: r.total>0 ? Math.round((r.present/r.total)*100) : 0 }));
  const { sorted: sortedSummary, toggleSort: toggleSummarySort, sortArrow: summaryArrow } = useSort(summaryWithPct, "date", "asc");

  const drillTotals = drillRows.reduce((a,r)=>{ a[r.status]=(a[r.status]||0)+1; return a; }, {});
  const visibleDrill = drillRows
    .filter(r => !statusFilter || r.status===statusFilter)
    .sort((a,b) => {
      const av=(a[sortField]||"").toString().toLowerCase();
      const bv=(b[sortField]||"").toString().toLowerCase();
      return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
    });

  const handleSort = (f) => { if (sortField===f) setSortAsc(p=>!p); else { setSortField(f); setSortAsc(true); } };

  const SortArrow = ({ f }) => sortField===f
    ? (sortAsc ? <FaChevronUp size={8} style={{marginLeft:3}}/> : <FaChevronDown size={8} style={{marginLeft:3}}/>)
    : <FaChevronDown size={8} style={{marginLeft:3,opacity:0.20}}/>;

  const TH = ({ children, field, align="left" }) => (
    <th onClick={()=>field&&handleSort(field)}
      style={{ padding:"10px 14px", fontSize:10, fontWeight:800,
        color:T.textMuted, textTransform:"uppercase",
        letterSpacing:"0.08em", fontFamily:fontFam, textAlign:align, whiteSpace:"nowrap",
        background: sortField===field ? T.glass : T.rowOdd,
        borderBottom:T.rowBdr,
        cursor:field?"pointer":"default", userSelect:"none" }}>
      {children}{field && <SortArrow f={field}/>}
    </th>
  );

  const quickFetch = (n) => {
    const f=daysAgo(n), t=today();
    setFromDate(f); setToDate(t);
    setLoading(true); setError(null); setDrillDate(null); setDrillRows([]);
    // FIX: use from_date / to_date
    const params = new URLSearchParams({ from_date:f, to_date:t });
    if (batchId) params.append("batch_id", batchId);
    fetch(`${BASE}/attendance/summary?${params}`)
      .then(r=>r.ok?r.json():Promise.reject())
      .then(d=>setSummary(d.summary||[]))
      .catch(()=>setError("Could not load summary."))
      .finally(()=>setLoading(false));
  };

  // ── Attendance % colour ──────────────────────────────────────────────────────
  const pctColor = p => p>=80?getBrandColors().green:p>=60?getBrandColors().orange:getBrandColors().red;

  return (
    <div style={{ padding:"24px 28px 60px", fontFamily:fontFam, background:T.pageBg, minHeight:"100dvh" }}>

      {/* ── Header ── */}
      <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:24 }}>
        <PageHeaderIcon icon={<FaChartBar size={20} color={getBrandColors().blueDark} />} />
        <div style={{ flex:1 }}>
          <h2 style={{ margin:0, fontSize:22, fontWeight:900,
            color:T.textPrimary, fontFamily:fontFam, letterSpacing:"-0.02em" }}>
            Attendance Report
          </h2>
          <p style={{ margin:"2px 0 0", fontSize:12, color:T.textMuted }}>
            Date-range summary · click any row to drill into student-level records
          </p>
        </div>
        <button onClick={()=>window.print()}
          style={{ display:"flex", alignItems:"center", gap:7, padding:"9px 18px",
            borderRadius:10, border:`1.5px solid ${T.inputBdr}`,
            background:T.glass, backdropFilter:"blur(10px)", WebkitBackdropFilter: "blur(10px)",
            WebkitBackdropFilter:"blur(10px)", color:T.textSub,
            fontWeight:700, fontSize:13, fontFamily:fontFam, cursor:"pointer" }}>
          <FaPrint size={13}/> Print
        </button>
      </div>

      {/* ── Filters ── */}
      <div style={{ ...gc, padding:"14px 20px", marginBottom:20,
        display:"flex", gap:12, flexWrap:"wrap", alignItems:"center" }}>

        <label style={{ fontSize:11, fontWeight:700, color:T.textMuted,
          textTransform:"uppercase", letterSpacing:"0.07em" }}>From</label>
        <input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)}
          style={inputStyle(T)}/>

        <label style={{ fontSize:11, fontWeight:700, color:T.textMuted,
          textTransform:"uppercase", letterSpacing:"0.07em" }}>To</label>
        <input type="date" value={toDate} onChange={e=>setToDate(e.target.value)}
          style={inputStyle(T)}/>

        <div style={{ display:"flex", alignItems:"center", gap:7, padding:"8px 12px", borderRadius:9,
          border:`1.5px solid ${T.inputBdr}`, background:T.inputBg,
          backdropFilter:"blur(10px)", WebkitBackdropFilter:"blur(10px)" }}>
          <FaLayerGroup size={12} style={{ color:secondary }}/>
          <select value={batchId} onChange={e=>setBatchId(e.target.value)}
            style={{ border:"none", outline:"none", background:"transparent",
              fontSize:13, color:T.inputText, fontFamily:fontFam,
              cursor:"pointer", minWidth:130, colorScheme: T.colorScheme }}>
            <option value="">All Batches</option>
            {batches.map(b=><option key={b.batch_id} value={b.batch_id}>{b.batch_name}</option>)}
          </select>
        </div>

        <button onClick={fetchSummary}
          style={{ padding:"9px 22px", borderRadius:10,
            background:`linear-gradient(135deg,${secondary},${secondary}cc)`,
            color:getBrandColors().blueDark, fontWeight:800, fontSize:13, fontFamily:fontFam,
            border:"none", cursor:"pointer",
            display:"flex", alignItems:"center", gap:7,
            boxShadow:`0 4px 16px ${secondary}40` }}>
          <FaFilter size={11}/> Apply
        </button>

        <div style={{ display:"flex", gap:6, marginLeft:"auto" }}>
          {[{l:"7d",n:7},{l:"30d",n:30},{l:"90d",n:90}].map(q=>(
            <button key={q.l} onClick={()=>quickFetch(q.n)}
              style={{ padding:"7px 14px", borderRadius:8,
                border:`1.5px solid ${T.inputBdr}`,
                background:T.glass,
                color:T.textSub, fontWeight:700,
                fontSize:12, fontFamily:fontFam, cursor:"pointer" }}>
              {q.l}
            </button>
          ))}
        </div>
      </div>

      {/* ── Stat cards ── */}
      {!loading && summary.length>0 && (
        <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:20 }}>
          <StatCard icon={FaCalendarAlt} label="Sessions" value={summary.length}
            color={secondary} glowColor={secondary}/>
          <StatCard icon={FaUsers} label="Records" value={totals.total}
            color={T.textSub} glowColor={T.textMuted}/>
          <StatCard icon={FaCheckCircle} label="Present" value={totals.present}
            color={getBrandColors().green} glowColor={getBrandColors().green}/>
          <StatCard icon={FaTimesCircle} label="Absent"  value={totals.absent}
            color={getBrandColors().red} glowColor={getBrandColors().red}/>
          <StatCard icon={FaClock}       label="Late"    value={totals.late}
            color={getBrandColors().orange} glowColor={getBrandColors().orange}/>
          <div style={{ ...gc, padding:"14px 20px", display:"flex",
            alignItems:"center", gap:14, flex:1, minWidth:140 }}>
            <div style={{ width:40, height:40, borderRadius:11, flexShrink:0,
              background:`${pctColor(overallPct)}18`,
              border:`1px solid ${pctColor(overallPct)}35`,
              display:"flex", alignItems:"center", justifyContent:"center",
              boxShadow:`0 0 14px ${pctColor(overallPct)}25` }}>
              <FaUserCheck size={17} style={{ color:pctColor(overallPct) }}/>
            </div>
            <div>
              <div style={{ fontSize:26, fontWeight:900, lineHeight:1,
                color:pctColor(overallPct),
                textShadow:`0 0 16px ${pctColor(overallPct)}60` }}>{overallPct}%</div>
              <div style={{ fontSize:10, fontWeight:700, color:T.textMuted,
                textTransform:"uppercase", letterSpacing:"0.08em", marginTop:2 }}>Attendance</div>
            </div>
          </div>
        </div>
      )}

      {/* ── Error ── */}
      {error && (
        <div style={{ ...gc, padding:"12px 18px", marginBottom:16,
          background:"rgba(248,113,113,0.10)", border:"1px solid rgba(248,113,113,0.30)",
          color:getBrandColors().red, fontSize:13, fontFamily:fontFam,
          display:"flex", alignItems:"center", gap:9 }}>
          <FaExclamationCircle size={14}/> {error}
        </div>
      )}

      {/* ── Loading ── */}
      {loading && (
        <div style={{ textAlign:"center", padding:"60px 24px", color:T.textMuted, fontSize:13 }}>
          Loading…
        </div>
      )}

      {/* ── Empty state ── */}
      {!loading && summary.length===0 && !error && (
        <div style={{ ...gc, padding:"60px 24px", textAlign:"center" }}>
          <FaCalendarAlt size={34} style={{ opacity:0.15, display:"block",
            margin:"0 auto 14px", color:secondary }}/>
          <p style={{ fontSize:14, color:T.textMuted, margin:0 }}>
            No attendance records found for the selected period.
          </p>
        </div>
      )}

      {/* ── Summary table with inline drill-down ── */}
      {!loading && summary.length>0 && (
        <div style={{ ...gc, overflow:"hidden" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr>
                {[
                  { h:"Date",         align:"left",   field:"date"    },
                  { h:"Present",      align:"center", field:"present" },
                  { h:"Absent",       align:"center", field:"absent"  },
                  { h:"Late",         align:"center", field:"late"    },
                  { h:"Total",        align:"center", field:"total"   },
                  { h:"Attendance %", align:"left", w:180, field:"pct" },
                ].map(col=>(
                  <th key={col.h} onClick={()=>toggleSummarySort(col.field)}
                    style={{ padding:"11px 16px", fontSize:10, fontWeight:800,
                      color:T.textMuted, textTransform:"uppercase",
                      letterSpacing:"0.08em", fontFamily:fontFam,
                      textAlign:col.align, width:col.w,
                      borderBottom:T.rowBdr,
                      background:T.rowOdd, whiteSpace:"nowrap",
                      cursor:"pointer", userSelect:"none" }}>
                    {col.h}{summaryArrow(col.field)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedSummary.map((row,i) => {
                const pct = row.total>0 ? Math.round((row.present/row.total)*100) : 0;
                const on  = drillDate===row.date;
                return (
                  <React.Fragment key={row.date}>
                    {/* Summary row */}
                    <tr onClick={()=>fetchDrill(row.date)}
                      style={{ background:on?T.glass:i%2===0?T.rowEven:T.rowOdd,
                        borderLeft:`3px solid ${on?secondary:"transparent"}`,
                        cursor:"pointer", transition:"background 0.15s" }}
                      onMouseEnter={e=>{ if(!on) e.currentTarget.style.background=T.rowOdd; }}
                      onMouseLeave={e=>{ if(!on) e.currentTarget.style.background=i%2===0?T.rowEven:T.rowOdd; }}>

                      <td style={{ padding:"13px 16px", borderBottom:T.rowBdr }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <div style={{ width:8, height:8, borderRadius:"50%", flexShrink:0,
                            background:on?secondary:"transparent",
                            border:`2px solid ${on?secondary:T.inputBdr}`,
                            boxShadow:on?`0 0 8px ${secondary}80`:"none" }}/>
                          <div>
                            <span style={{ fontSize:14, fontWeight:700,
                              color:T.inputText, fontFamily:fontFam }}>
                              {fmtDate(row.date)}
                            </span>
                            <span style={{ fontSize:11, color:T.textMuted, marginLeft:8 }}>{row.date}</span>
                          </div>
                          {on && (
                            <span style={{ fontSize:10, color:secondary, fontWeight:700,
                              background:`${secondary}18`, padding:"2px 9px",
                              borderRadius:6, marginLeft:4, border:`1px solid ${secondary}35` }}>
                              ▲ EXPANDED
                            </span>
                          )}
                        </div>
                      </td>

                      <td style={{ padding:"13px 16px", textAlign:"center",
                        borderBottom:T.rowBdr,
                        fontSize:15, fontWeight:800, color:getBrandColors().green, fontFamily:fontFam }}>
                        {row.present}
                      </td>
                      <td style={{ padding:"13px 16px", textAlign:"center",
                        borderBottom:T.rowBdr,
                        fontSize:15, fontWeight:800, color:getBrandColors().red, fontFamily:fontFam }}>
                        {row.absent}
                      </td>
                      <td style={{ padding:"13px 16px", textAlign:"center",
                        borderBottom:T.rowBdr,
                        fontSize:15, fontWeight:800, color:getBrandColors().orange, fontFamily:fontFam }}>
                        {row.late}
                      </td>
                      <td style={{ padding:"13px 16px", textAlign:"center",
                        borderBottom:T.rowBdr,
                        fontSize:15, fontWeight:700,
                        color:T.textSub, fontFamily:fontFam }}>
                        {row.total}
                      </td>
                      <td style={{ padding:"13px 16px",
                        borderBottom:T.rowBdr, minWidth:160 }}>
                        <PctBar pct={pct}/>
                      </td>
                    </tr>

                    {/* ── Inline detail panel ── */}
                    {on && (
                      <tr>
                        <td colSpan={6} style={{ padding:0,
                          borderBottom:`2px solid ${secondary}25` }}>
                          <div style={{
                            background:"rgba(0,0,0,0.30)",
                            backdropFilter:"blur(10px)", WebkitBackdropFilter:"blur(10px)",
                            borderTop:`2px solid ${secondary}25`,
                          }}>

                            {/* Detail toolbar */}
                            <div style={{ display:"flex", alignItems:"center", gap:10,
                              padding:"12px 20px",
                              borderBottom:T.rowBdr }}>
                              <FaSortAmountDown size={13} style={{ color:secondary }}/>
                              <span style={{ fontSize:13, fontWeight:800,
                                color:T.inputText, fontFamily:fontFam }}>
                                Detail — {fmtDate(drillDate)}
                              </span>
                              <span style={{ fontSize:11, color:T.textMuted }}>
                                ({drillRows.length} records)
                              </span>

                              {/* Filter pills */}
                              <div style={{ display:"flex", gap:6, marginLeft:12 }}>
                                {["","Present","Absent","Late","Excused"].map(s => {
                                  const m = s ? statusMeta(s, T) : null;
                                  const active = statusFilter===s;
                                  return (
                                    <button key={s}
                                      onClick={e=>{ e.stopPropagation(); setStatusFilter(s); }}
                                      style={{ padding:"4px 12px", borderRadius:20,
                                        fontSize:11, fontWeight:700,
                                        cursor:"pointer", fontFamily:fontFam,
                                        border:"1.5px solid", transition:"all 0.15s",
                                        background: active ? (m?m.bg:T.glass) : T.rowOdd,
                                        color:      active ? (m?m.color:T.textPrimary) : T.textMuted,
                                        borderColor:active ? (m?m.border:T.inputBdr) : T.inputBdr }}>
                                      {s||"All"}
                                      {s && drillTotals[s] ? ` (${drillTotals[s]})` : ""}
                                    </button>
                                  );
                                })}
                              </div>
                              <div style={{ flex:1 }}/>
                              <FaTimes size={13} style={{ color:T.textMuted, cursor:"pointer" }}
                                onClick={e=>{ e.stopPropagation(); setDrillDate(null); }}/>
                            </div>

                            {drillLoad && (
                              <div style={{ padding:"30px", textAlign:"center",
                                color:T.textMuted, fontSize:13 }}>
                                Loading records…
                              </div>
                            )}

                            {!drillLoad && drillRows.length===0 && (
                              <div style={{ padding:"30px", textAlign:"center",
                                color:T.textMuted, fontSize:13 }}>
                                No records for this date.
                              </div>
                            )}

                            {!drillLoad && drillRows.length>0 && (
                              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                                <thead>
                                  <tr>
                                    <TH align="center">#</TH>
                                    <TH field="student_name">Student Name</TH>
                                    <TH field="student_code">Code</TH>
                                    <TH field="batch_name">Batch</TH>
                                    <TH field="status">Status</TH>
                                    <TH field="remarks">Remarks</TH>
                                    <TH field="recorded_by">Recorded By</TH>
                                  </tr>
                                </thead>
                                <tbody>
                                  {visibleDrill.map((r, idx) => {
                                    const initials = (r.student_name||"?").split(" ").map(w=>w[0]).slice(0,2).join("").toUpperCase();
                                    return (
                                      <tr key={r.attendance_id||r.student_id||idx}
                                        style={{ background:idx%2===0?T.rowEven:T.rowOdd,
                                          borderBottom:T.rowBdr,
                                          transition:"background 0.1s" }}
                                        onMouseEnter={e=>e.currentTarget.style.background=T.glass}
                                        onMouseLeave={e=>e.currentTarget.style.background=idx%2===0?T.rowEven:T.rowOdd}>

                                        <td style={{ padding:"10px 14px", textAlign:"center",
                                          fontSize:11, color:T.textMuted, fontFamily:fontFam, width:40 }}>
                                          {idx+1}
                                        </td>

                                        <td style={{ padding:"10px 16px" }}>
                                          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                                            <div style={{ width:32, height:32, borderRadius:"50%", flexShrink:0,
                                              background:`linear-gradient(135deg,${secondary}cc,${secondary}55)`,
                                              display:"flex", alignItems:"center", justifyContent:"center",
                                              fontSize:11, fontWeight:900, color:getBrandColors().blueDark,
                                              boxShadow:`0 0 10px ${secondary}40` }}>
                                              {initials}
                                            </div>
                                            <span style={{ fontSize:13, fontWeight:700,
                                              color:T.inputText, fontFamily:fontFam }}>
                                              {r.student_name || "—"}
                                            </span>
                                          </div>
                                        </td>

                                        <td style={{ padding:"10px 14px", fontSize:12, fontWeight:700,
                                          color:secondary, fontFamily:fontFam }}>
                                          {r.student_code || <span style={{ color:T.textMuted }}>—</span>}
                                        </td>

                                        <td style={{ padding:"10px 14px", fontSize:12,
                                          color:T.textSub, fontFamily:fontFam }}>
                                          {r.batch_name || <span style={{ color:T.textMuted }}>—</span>}
                                        </td>

                                        <td style={{ padding:"10px 14px" }}>
                                          <StatusBadge status={r.status}/>
                                        </td>

                                        <td style={{ padding:"10px 14px", fontSize:12,
                                          color:T.textMuted, fontFamily:fontFam, maxWidth:220 }}>
                                          {r.remarks
                                            ? <span style={{ color:T.textSub }}>{r.remarks}</span>
                                            : <span style={{ color:T.textMuted, fontStyle:"italic" }}>—</span>}
                                        </td>

                                        <td style={{ padding:"10px 14px", fontSize:11,
                                          color:T.textMuted, fontFamily:fontFam }}>
                                          {r.recorded_by || <span style={{ color:T.textMuted, fontStyle:"italic" }}>—</span>}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}

                            {/* Detail footer */}
                            {!drillLoad && drillRows.length>0 && (
                              <div style={{ display:"flex", gap:20, padding:"10px 20px",
                                borderTop:T.rowBdr,
                                background:T.rowOdd }}>
                                {Object.entries(drillTotals).map(([status,count]) => {
                                  const m = statusMeta(status, T)||{ color:T.textMuted };
                                  return (
                                    <span key={status} style={{ fontSize:12, fontFamily:fontFam,
                                      display:"flex", alignItems:"center", gap:5 }}>
                                      <strong style={{ color:m.color }}>{count}</strong>
                                      <span style={{ color:T.textMuted }}>{status}</span>
                                    </span>
                                  );
                                })}
                                <span style={{ fontSize:12, fontFamily:fontFam,
                                  color:T.textMuted, marginLeft:"auto" }}>
                                  Showing{" "}
                                  <strong style={{ color:T.textSub }}>{visibleDrill.length}</strong>
                                  {" "}of{" "}
                                  <strong style={{ color:T.textSub }}>{drillRows.length}</strong>
                                  {" "}records
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
